import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import usePagedRegister from "../usePagedRegister";
import "./ClaimsWorkspace.css";

const rows = (value) =>
  Array.isArray(value) ? value : value?.results || [];
const money = (value) => Number(value || 0).toFixed(2);

function ClaimsWorkspace() {
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [transition, setTransition] = useState(null);
  const [transitionReason, setTransitionReason] = useState("");
  const editableStatuses = ["DRAFT", "ACTIVE", "UNDER REVIEW"];

  const {
    items: claims,
    reload: reloadClaims,
    onScroll: claimScroll,
  } = usePagedRegister("claims/", {
    search,
    extraParams: statusFilter ? { status: statusFilter } : {},
    pageSize: 50,
  });

  const load = async () => reloadClaims({ append: false });

  const openClaim = async (claim) => {
    setSelected(claim);
    try {
      const response = await axiosInstance.get(`claims/${claim.id}/`);
      setSelected(response.data);
    } catch {
      setSelected(claim);
    }
  };


  const startEdit = () => {
    setEditForm({
      claim_date: selected.claim_date || "",
      incident_date: selected.incident_date || "",
      plan_position: selected.plan_position || "",
      burial_order_number: selected.burial_order_number || "",
      death_register_number: selected.death_register_number || "",
      burial_place: selected.burial_place || "",
      burial_service_by: selected.burial_service_by || "",
      burial_service_date: selected.burial_service_date || "",
      removal_time: selected.removal_time || "",
      doctor_used: selected.doctor_used || "",
      remarks: selected.remarks || "",
      notes: selected.notes || "",
      reason: "",
    });
    setEditing(true);
  };

  const setField = (field, value, uppercase = false) => {
    setEditForm((current) => ({
      ...current,
      [field]: uppercase ? value.toUpperCase() : value,
    }));
  };

  const saveClaim = async (event) => {
    event.preventDefault();
    if (!editForm.reason?.trim()) {
      toast.error("State the reason for editing this claim.");
      return;
    }
    try {
      setSaving(true);
      const response = await axiosInstance.patch(
        `claims/${selected.id}/`, editForm
      );
      setSelected(response.data);
      setEditing(false);
      await reloadClaims({ append: false });
      toast.success(`Claim ${response.data.code} updated.`);
    } catch (error) {
      const data = error?.response?.data || {};
      toast.error(
        typeof data.detail === "string"
          ? data.detail
          : Object.values(data).flat().join(" ") ||
            "Could not update the claim."
      );
    } finally {
      setSaving(false);
    }
  };

  const startTransition = (action) => {
    setTransition(action);
    setTransitionReason("");
  };

  const applyTransition = async (event) => {
    event.preventDefault();
    if (!transitionReason.trim()) {
      toast.error("State the reason for this claim action.");
      return;
    }
    try {
      setSaving(true);
      const response = await axiosInstance.post(
        `claims/${selected.id}/transition/`,
        { action: transition.action, reason: transitionReason }
      );
      setSelected(response.data);
      setTransition(null);
      setTransitionReason("");
      await reloadClaims({ append: false });
      toast.success(
        `Claim ${response.data.code} moved to ${response.data.status}.`
      );
    } catch (error) {
      const data = error?.response?.data || {};
      toast.error(
        typeof data.detail === "string"
          ? data.detail
          : "Could not complete the claim action."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="claims-page">
      <header className="claims-title">
        <span>ASSURANCE OPERATIONS</span>
        <h1>Claims</h1>
        <p>Registered claims, eligibility outcomes and benefit snapshots.</p>
      </header>

      <div className="claims-toolbar">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && load()}
          placeholder="Claim, policy, deceased member or national ID"
        />
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="">All statuses</option>
          {["DRAFT","ACTIVE","UNDER REVIEW","APPROVED","REJECTED","SETTLED","CANCELLED"].map(
            (status) => <option key={status}>{status}</option>
          )}
        </select>
        <button onClick={load}>Search</button>
      </div>

      <div className="claims-layout">
        <aside className="claims-register" onScroll={claimScroll}>
          {claims.length ? claims.map((claim) => (
            <button
              key={claim.id}
              className={selected?.id === claim.id ? "active" : ""}
              onClick={() => openClaim(claim)}
            >
              <strong>{claim.code}</strong>
              <span>{claim.deceased_member_name}</span>
              <small>Policy #{claim.policy} · {claim.claim_date}</small>
              <b>{claim.status} · Total {money(claim.total_claim_cost)}</b>
            </button>
          )) : <div className="claims-empty">No claims found.</div>}
        </aside>

        <main className="claim-workspace">
          {!selected ? <div className="claims-empty">Select a claim.</div> : (
            <>
              <header className="claim-head">
                <div>
                  <span>CLAIM RECORD</span>
                  <h2>{selected.code}</h2>
                  <p>{selected.deceased_member_name}</p>
                </div>
                <div className="claim-head-actions">
                  <strong className="claim-status">{selected.status}</strong>
                  <button
                    type="button"
                    className="claim-edit-button"
                    onClick={startEdit}
                    disabled={!editableStatuses.includes(selected.status)}
                    title={editableStatuses.includes(selected.status)
                      ? "Edit claim details"
                      : `${selected.status} claims are locked`}
                  >
                    <i className="bi bi-pencil-square"></i>
                    Edit claim
                  </button>
                </div>
              </header>

              <div className="claim-summary">
                {[
                  ["Policy ID", selected.policy],
                  ["Claim date", selected.claim_date],
                  ["Incident date", selected.incident_date],
                  ["Cash allowance", money(selected.cash_allowance)],
                  ["Benefit/service costs", money(selected.benefit_service_cost)],
                  ["Total claim cost", money(selected.total_claim_cost)],
                  ["Plan position", selected.plan_position || "—"],
                  ["Payment method", selected.payment_method || "—"],
                ].map(([label, value]) => (
                  <article key={label}>
                    <span>{label}</span><strong>{value}</strong>
                  </article>
                ))}
              </div>

              <section className="claim-section">
                <h3>Claim and burial information</h3>
                <div className="claim-detail-grid">
                  {[
                    ["Member number", selected.deceased_member_code],
                    ["Burial order", selected.burial_order_number],
                    ["Death register", selected.death_register_number],
                    ["Burial place", selected.burial_place],
                    ["Burial service by", selected.burial_service_by],
                    ["Burial service date", selected.burial_service_date],
                    ["Removal time", selected.removal_time],
                    ["Doctor used", selected.doctor_used],
                    ["Remarks", selected.remarks],
                    ["Notes", selected.notes],
                  ].map(([label, value]) => (
                    <article key={label}>
                      <span>{label}</span><strong>{value || "—"}</strong>
                    </article>
                  ))}
                </div>
              </section>

              <section className="claim-section">
                <h3>Compilation and authorisation</h3>
                <div className="claim-detail-grid">
                  {[
                    ["Compiled by", selected.compiled_by_name],
                    ["Compile date", selected.compile_date],
                    ["Checked by", selected.checked_by_name || "—"],
                    ["Check date", selected.check_date],
                    ["Approved by", selected.approved_by_name || "—"],
                    ["Branch", selected.branch],
                  ].map(([label, value]) => (
                    <article key={label}>
                      <span>{label}</span><strong>{value || "—"}</strong>
                    </article>
                  ))}
                </div>
              </section>

              <section className="claim-section claim-workflow-section">
                <div className="claim-workflow-heading">
                  <div>
                    <span>CONTROLLED WORKFLOW</span>
                    <h3>Next claim action</h3>
                    <p>Each decision is permission-controlled and recorded in the audit trail.</p>
                  </div>
                  <strong>{selected.status}</strong>
                </div>
                {(selected.available_actions || []).length ? (
                  <div className="claim-workflow-actions">
                    {selected.available_actions.map((action) => (
                      <button
                        type="button"
                        key={action.action}
                        className={`workflow-${action.action.toLowerCase()}`}
                        onClick={() => startTransition(action)}
                      >
                        <i className="bi bi-arrow-right-circle"></i>
                        <span><strong>{action.label}</strong><small>{action.help}</small></span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="claim-workflow-empty">
                    {['SETTLED','REJECTED','CANCELLED'].includes(selected.status)
                      ? `This claim reached the final status ${selected.status}.`
                      : "The next action requires an authorised claims user."}
                  </div>
                )}
              </section>

              <section className="claim-section">
                <h3>Benefit snapshots</h3>
                <div className="claim-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Benefit</th><th>Description</th>
                        <th>Type</th><th>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selected.benefits || []).length ? (
                        selected.benefits.map((benefit) => (
                          <tr key={benefit.id}>
                            <td>{benefit.benefit_code}</td>
                            <td>{benefit.description}</td>
                            <td>{benefit.benefit_type}</td>
                            <td>{money(benefit.cost)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan="4">No benefit snapshots.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <footer className="claim-note">
                Claim documents, stock issues, checker/authoriser actions,
                accounting posting and reversal remain controlled claim phases.
              </footer>
            </>
          )}
        </main>
      </div>

      {editing && (
        <div className="claim-modal-backdrop">
          <form className="claim-edit-modal" onSubmit={saveClaim}>
            <header>
              <div><span>EDIT CLAIM</span><h2>{selected.code}</h2>
                <p>{selected.deceased_member_name} · Policy #{selected.policy}</p>
              </div>
              <button type="button" className="claim-modal-close"
                onClick={() => setEditing(false)} disabled={saving}>×</button>
            </header>
            <div className="claim-edit-notice">
              <i className="bi bi-shield-check"></i>
              <span>Policy, deceased member, payout, benefits and approval information remain protected.</span>
            </div>
            <div className="claim-edit-grid">
              <label><span>Claim date</span><input type="date" required value={editForm.claim_date} onChange={(e) => setField("claim_date", e.target.value)} /></label>
              <label><span>Incident date</span><input type="date" required value={editForm.incident_date} onChange={(e) => setField("incident_date", e.target.value)} /></label>
              <label><span>Plan position</span><input value={editForm.plan_position} onChange={(e) => setField("plan_position", e.target.value, true)} /></label>
              <label><span>Burial order number</span><input value={editForm.burial_order_number} onChange={(e) => setField("burial_order_number", e.target.value, true)} /></label>
              <label><span>Death register number</span><input value={editForm.death_register_number} onChange={(e) => setField("death_register_number", e.target.value, true)} /></label>
              <label><span>Burial place</span><input value={editForm.burial_place} onChange={(e) => setField("burial_place", e.target.value, true)} /></label>
              <label><span>Burial service by</span><input value={editForm.burial_service_by} onChange={(e) => setField("burial_service_by", e.target.value, true)} /></label>
              <label><span>Burial service date</span><input type="date" value={editForm.burial_service_date} onChange={(e) => setField("burial_service_date", e.target.value)} /></label>
              <label><span>Removal time</span><input type="time" value={editForm.removal_time} onChange={(e) => setField("removal_time", e.target.value)} /></label>
              <label><span>Doctor used</span><input value={editForm.doctor_used} onChange={(e) => setField("doctor_used", e.target.value, true)} /></label>
              <label className="claim-edit-wide"><span>Remarks</span><textarea rows="3" value={editForm.remarks} onChange={(e) => setField("remarks", e.target.value, true)} /></label>
              <label className="claim-edit-wide"><span>Notes</span><textarea rows="3" value={editForm.notes} onChange={(e) => setField("notes", e.target.value, true)} /></label>
              <label className="claim-edit-wide claim-reason"><span>Reason for this change *</span><textarea rows="2" required value={editForm.reason} onChange={(e) => setField("reason", e.target.value, true)} placeholder="Explain why the claim record is being updated" /></label>
            </div>
            <footer>
              <button type="button" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>{saving ? "Saving..." : "Save claim changes"}</button>
            </footer>
          </form>
        </div>
      )}
      {transition && (
        <div className="claim-modal-backdrop">
          <form className="claim-transition-modal" onSubmit={applyTransition}>
            <header>
              <div>
                <span>CLAIM WORKFLOW</span>
                <h2>{transition.label}</h2>
                <p>{selected.code} · {selected.status} → {transition.target_status}</p>
              </div>
              <button type="button" onClick={() => setTransition(null)} disabled={saving}>×</button>
            </header>
            <div className="claim-transition-body">
              <div><i className="bi bi-shield-check"></i><p>{transition.help}</p></div>
              <label>
                <span>Decision reason *</span>
                <textarea
                  rows="4"
                  required
                  autoFocus
                  value={transitionReason}
                  onChange={(event) => setTransitionReason(event.target.value.toUpperCase())}
                  placeholder="Record the reason and supporting decision"
                />
              </label>
            </div>
            <footer>
              <button type="button" onClick={() => setTransition(null)} disabled={saving}>Cancel</button>
              <button type="submit" className="primary" disabled={saving}>
                {saving ? "Processing..." : `Confirm ${transition.label}`}
              </button>
            </footer>
          </form>
        </div>
      )}
    </section>
  );
}

export default ClaimsWorkspace;
